import { FormControlLabel, Stack, Switch } from "@mui/material";
import { CognitoUser, CognitoUserPoolType, UserAccountsService } from "gen/clients";
import * as React from "react";
import { useMutation } from "react-query";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { useDialogState } from "hooks/useDialogState";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { InfoIcon } from "components/InfoIcon/InfoIcon";

interface Props {
  cognitoUser: CognitoUser;
  onSuccess: () => void;
}

const MfaEnabledSwitch: React.FC<Props> = ({ cognitoUser, onSuccess }) => {
  const [isDisableMfaAlertDialogOpen, openDisableMfaAlertDialog, closeDisableMfaAlertDialog] = useDialogState();
  const {
    mutate,
    error: mutateError,
    isLoading: isMutateLoading
  } = useMutation({
    mutationFn: UserAccountsService.changeMfaSettings,
    onSuccess: () => {
      closeDisableMfaAlertDialog();
      onSuccess();
    }
  });

  const onClick = React.useCallback(
    event => {
      const isEnablingMfa = event.target.checked;
      if (isEnablingMfa) {
        mutate({
          userPool: CognitoUserPoolType.INTERNAL_USER_POOL,
          cognitoUsername: cognitoUser.username,
          requestBody: {
            emailMfaSettings: { isEnabled: false },
            smsMfaSettings: { isEnabled: true },
            softwareTokenMfaSettings: { isEnabled: false }
          }
        });
      } else {
        openDisableMfaAlertDialog();
      }
    },
    [mutate, cognitoUser.username, openDisableMfaAlertDialog]
  );

  return (
    <>
      {mutateError && <SnackbarApiError error={mutateError} />}
      <FormControlLabel
        control={
          <Switch
            checked={cognitoUser?.isMfaEnabled || false}
            onChange={onClick}
            disabled={isMutateLoading || !cognitoUser || !cognitoUser.phoneNumber}
            size="small"
          />
        }
        label={
          <Stack direction="row" spacing={1}>
            <div>{cognitoUser?.isMfaEnabled ? "Yes" : "No"}</div>
            {!cognitoUser.phoneNumber && (
              <InfoIcon tooltip="Phone number is required to enable MFA. Add it by clicking on the Edit Attributes button." />
            )}
          </Stack>
        }
        labelPlacement="end"
        sx={{ ml: 1 }}
      />
      {isDisableMfaAlertDialogOpen && (
        <ConfirmationDialog
          title="Disable MFA"
          content={<div>Are you sure you want to disable MFA for this user?</div>}
          onClose={closeDisableMfaAlertDialog}
          onConfirm={() => {
            mutate({
              userPool: CognitoUserPoolType.INTERNAL_USER_POOL,
              cognitoUsername: cognitoUser.username,
              requestBody: {
                emailMfaSettings: { isEnabled: false },
                smsMfaSettings: { isEnabled: false },
                softwareTokenMfaSettings: { isEnabled: false }
              }
            });
          }}
          isLoading={isMutateLoading}
          error={!!mutateError && <ApiErrorMessage apiError={mutateError} />}
          confirmationButtonColor="error"
        />
      )}
    </>
  );
};

export { MfaEnabledSwitch };
